import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Avatar,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import { 
    PhotoCamera, 
    Delete,
    Upload
} from '@mui/icons-material';
import { useNotify, useTranslate, useRecordContext, useInput } from 'react-admin';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';
import { 
    resizeImageToMultipleSizes, 
    cleanupImageUrls, 
    validateImageFile,
    IMAGE_SIZES 
} from '../../utils/imageResizer';

interface ChargerImageUploadFieldProps {
    source: string;
    label?: string;
    required?: boolean;
    helperText?: string;
}

export const ChargerImageUploadField: React.FC<ChargerImageUploadFieldProps> = ({
    source,
    label,
    required = false,
    helperText,
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
    const notify = useNotify();
    const translate = useTranslate();
    const record = useRecordContext();
    
    // Use React Admin's useInput hook to properly integrate with the form
    const {
        field,
        fieldState: { error },
    } = useInput({ source });
    
    // Get chargerId from record (for edit mode) or generate temp ID for create mode
    const chargerId = record?.id || 'temp-' + Date.now();
    
    // Determine what image to display
    const displayValue = tempImageUrl || field.value;

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Clear previous error
        setUploadError(null);
        setUploadProgress(0);

        // Validate file
        const validation = validateImageFile(file);
        if (!validation.valid) {
            setUploadError(validation.error || translate('resources.chargers.errors.invalidImageType'));
            return;
        }

        setUploading(true);
        
        try {
            // Step 1: Resize images on the frontend
            setUploadProgress(10);
            console.log('[ChargerImage] Starting image resize...');
            const resizedImages = await resizeImageToMultipleSizes(file, IMAGE_SIZES);
            console.log(`[ChargerImage] Resized to ${resizedImages.length} sizes`);
            
            setUploadProgress(30);

            // For edit mode, upload directly to backend
            if (record?.id) {
                // Step 2: Prepare FormData with all image sizes
                const formData = new FormData();
                formData.append('chargerId', String(chargerId));
                
                // Add each resized image to FormData
                resizedImages.forEach((resized) => {
                    formData.append(`file_${resized.suffix}`, resized.file);
                    console.log(`[ChargerImage] Added ${resized.suffix}: ${resized.file.size} bytes`);
                });
                
                // Add metadata
                formData.append('originalName', file.name);
                formData.append('originalSize', String(file.size));
                formData.append('fileType', file.type);

                setUploadProgress(50);

                // Step 3: Upload all sizes to backend
                const token = await tokenManager.getAccessToken();
                if (!token) {
                    setUploadError(translate('ra.auth.auth_check_error'));
                    setUploading(false);
                    return;
                }
                
                console.log('[ChargerImage] Uploading to backend...');
                const response = await fetch(`${API_CONFIG.baseURL}/api/backend/chargers/upload-images`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });

                const data = await response.json();
                setUploadProgress(90);

                if (!response.ok) {
                    throw new Error(data.error || 'Upload failed');
                }

                console.log('[ChargerImage] Upload successful:', data);
                
                // Step 4: Update the form value with the returned URLs
                // Store all sizes if available, otherwise just the single URL
                const imageValue = data.urls && typeof data.urls === 'object' 
                    ? JSON.stringify(data.urls)
                    : data.url;
                
                // Update the form field value properly
                field.onChange(imageValue);
                
                // Also store temporarily for immediate display
                setTempImageUrl(imageValue);
                
                setUploadProgress(100);
                notify('resources.chargers.notifications.imageUploaded', { type: 'info' });
            } else {
                // For create mode, just store the image data locally
                // Create object URLs for preview
                const previewUrl = URL.createObjectURL(resizedImages.find(r => r.suffix === 'medium')?.file || file);
                
                // Store as a data URL or temporary URL
                field.onChange(previewUrl);
                setTempImageUrl(previewUrl);
                
                setUploadProgress(100);
                notify('resources.chargers.notifications.imageReady', { type: 'info' });
            }
            
            // Cleanup object URLs to prevent memory leaks
            cleanupImageUrls(resizedImages);
        } catch (error: any) {
            console.error('Upload error:', error);
            setUploadError(error.message || translate('resources.chargers.errors.uploadFailed'));
            notify(error.message || 'Upload failed', { type: 'error' });
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }

        // Reset the input
        event.target.value = '';
    };

    const handleRemove = () => {
        field.onChange(null);
        setTempImageUrl(null);
        notify('resources.chargers.notifications.imageRemoved', { type: 'info' });
    };

    // Parse the image URL - it might be a JSON string with multiple sizes
    const getDisplayUrl = (val: string) => {
        if (!val) return '';
        try {
            const parsed = JSON.parse(val);
            // Use medium size for display in admin, fallback to original
            return parsed.medium || parsed.small || parsed.original || val;
        } catch {
            // Not JSON, return as is
            return val;
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            {label && (
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {label} {required && '*'}
                </Typography>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {displayValue ? (
                    <>
                        <Avatar
                            src={getDisplayUrl(displayValue)}
                            sx={{
                                width: { xs: 80, sm: 100 },
                                height: { xs: 80, sm: 100 },
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                            variant="rounded"
                        />
                        <Box>
                            <input
                                id={`${source}-upload`}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleFileUpload}
                                disabled={uploading}
                            />
                            <label htmlFor={`${source}-upload`}>
                                <Button
                                    variant="outlined"
                                    component="span"
                                    disabled={uploading}
                                    startIcon={uploading ? <CircularProgress size={20} /> : <PhotoCamera />}
                                    size="small"
                                    sx={{ mb: 1 }}
                                >
                                    {uploading 
                                        ? `${translate('resources.chargers.actions.uploading')} ${uploadProgress}%`
                                        : translate('resources.chargers.actions.changeImage')
                                    }
                                </Button>
                            </label>
                            <Box>
                                <IconButton
                                    onClick={handleRemove}
                                    disabled={uploading}
                                    size="small"
                                    color="error"
                                >
                                    <Delete />
                                </IconButton>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <>
                        <input
                            id={`${source}-upload`}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                            disabled={uploading}
                        />
                        <label htmlFor={`${source}-upload`}>
                            <Button
                                variant="contained"
                                component="span"
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
                                color="primary"
                            >
                                {uploading 
                                    ? `${translate('resources.chargers.actions.uploading')} ${uploadProgress}%`
                                    : translate('resources.chargers.actions.uploadImage')
                                }
                            </Button>
                        </label>
                    </>
                )}
            </Box>
            
            {uploading && uploadProgress > 0 && (
                <Box sx={{ width: '100%', mt: 2 }}>
                    <CircularProgress variant="determinate" value={uploadProgress} />
                </Box>
            )}
            
            {(uploadError || error) && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError(null)}>
                    {uploadError || error?.message}
                </Alert>
            )}
            
            {helperText && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};